import { Observable } from "rxjs";

import { Empleado } from "../model/empleado/empleado.model";

/** Opción de los catálogos de incidencias ISO 42001 (tipo, gravedad, prioridad...). */
export interface OpcionIso {
  value: string;
  label: string;
  description?: string;
}

/** Estados a los que avanza una incidencia IA o de seguridad desde su detalle o su asignación. */
export type EstadoSiguienteIso = "PROCESO" | "ATENDIDO" | "VOBO";

/** Porqués de la causa / origen, guardados como JSON {"1": "...", "2": "..."}. */
export type PorquesIso = Record<string, string>;

/** Comentario (observación) de una incidencia IA o de seguridad. */
export interface ComentarioIso {
  idComentarioIa?: number;
  idIncidenciaIa?: number;
  nombreUsuario?: string;
  comentario?: string;
  fechaComentario?: string | Date;
}

/** Movimiento del historial de una incidencia IA o de seguridad. */
export interface HistorialIso {
  accion?: string;
  campoModificado?: string;
  valorAnterior?: string;
  valorNuevo?: string;
  fechaCambio?: string | Date;
}

/** Campos que comparten las incidencias IA y las de seguridad. */
export interface IncidenciaIsoComun {
  idIncidenciaIa?: number;
  estadoEvento?: string;
  fechaApertura?: string | Date;
  fechaAperturaInicio?: string | Date;
  fechaAperturaFin?: string | Date;
  notificadoPor?: string;
  descripcionEvento?: string;
  causaOrigen?: string;
  gravedad?: string;
  prioridad?: string;
  accionCorrectiva?: string;
  fechaPlaneadaResolucion?: string | Date;
  fechaAtencion?: string | Date;
  idUsuarioResponsableSeguimiento?: number;
  idUsuarioAlta?: number;
}

/** Respuesta del backend de incidencias IA y de seguridad. */
export interface RespuestaIso<TIncidencia> {
  incidencias?: TIncidencia[];
  incidencia?: TIncidencia;
  comentarios?: ComentarioIso[];
  historial?: HistorialIso[];
  responsables?: Empleado[];
  idGenerado?: number;
  totalItems?: number;
  codigoError?: string;
  mensajeError?: string;
  advertencia?: string;
}

/** Peticiones que tienen igual IncidenciasIAService e IncidenciasSeguridadService. */
export interface ServicioIncidenciasIso<TIncidencia> {
  consultarKanban(filtro: TIncidencia, regInicial: number, regXPagina: number): Observable<RespuestaIso<TIncidencia>>;
  obtener(idIncidenciaIa: number): Observable<RespuestaIso<TIncidencia>>;
  consultarResponsables(): Observable<RespuestaIso<TIncidencia>>;
  guardar(incidencia: TIncidencia): Observable<RespuestaIso<TIncidencia>>;
  actualizar(incidencia: TIncidencia): Observable<RespuestaIso<TIncidencia>>;
  cambiarEstado(
    idIncidenciaIa: number,
    estadoEvento: EstadoSiguienteIso,
    incidencia?: TIncidencia,
    comentario?: ComentarioIso,
  ): Observable<RespuestaIso<TIncidencia>>;
  guardarComentario(comentario: ComentarioIso): Observable<RespuestaIso<TIncidencia>>;
}

/** Avisos para cuando el backend contesta una página HTML en lugar del servicio. */
export interface TextosErrorHttpIso {
  servicioNoEncontrado: string;
  respuestaNoJson: string;
}

/**
 * Mensaje de error de una petición: un aviso propio si la ruta no llega al controller (el backend
 * contesta HTML); si no, el mensaje del backend, el del error o el que se indica.
 */
export function mensajeErrorHttpIso(e: any, fallback: string, textos: TextosErrorHttpIso): string {
  if (typeof e?.error === "string" && e.error.includes("<!doctype html")) {
    return textos.servicioNoEncontrado;
  }
  if (e?.status === 200 && e?.message?.includes("parsing")) {
    return textos.respuestaNoJson;
  }
  return e?.error?.mensajeError || e?.error?.message || e?.message || fallback;
}

/** Texto de una opción del catálogo: "-" sin valor y el valor tal cual si no está en el catálogo. */
export function etiquetaOpcion(value: string | undefined, options: OpcionIso[]): string {
  if (!value) {
    return "-";
  }
  return options.find((option) => option.value === value)?.label || value;
}

function leerPorques(value: string | undefined, sinObjeto: (texto: string) => PorquesIso): PorquesIso {
  if (!value?.trim()) {
    return {};
  }
  try {
    const parsed = JSON.parse(value);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
      return sinObjeto(value);
    }
    return Object.keys(parsed).reduce<PorquesIso>((acc, key) => {
      const index = Number(key);
      if (Number.isInteger(index) && index >= 1) {
        acc[String(index)] = String(parsed[key] || "");
      }
      return acc;
    }, {});
  } catch {
    return sinObjeto(value);
  }
}

/** Porqués que muestra el detalle: un texto que no es un objeto JSON se muestra como el porqué 1. */
export function porquesDetalleIso(value?: string): PorquesIso {
  return leerPorques(value, (texto) => ({ "1": texto.trim() }));
}

/** Porqués que edita el formulario: un texto que no es un objeto JSON no trae porqués. */
export function porquesFormularioIso(value?: string): PorquesIso {
  return leerPorques(value, () => ({}));
}

/** Usuario del empleado: el del objeto usuario o el idUsuario plano. */
export function idUsuarioEmpleadoIso(empleado: Empleado): number | undefined {
  return empleado?.usuario?.id || empleado?.idUsuario;
}

/** Nombre del responsable: nombre completo, nombre de la persona o del usuario; "-" si no trae ninguno. */
export function nombreEmpleadoIso(empleado: Empleado): string {
  const persona = empleado?.persona;
  const nombrePersona = [persona?.nombre, persona?.apellidoPaterno, persona?.apellidoMaterno]
    .filter(Boolean)
    .join(" ")
    .trim();
  return (empleado as any)?.nombreCompleto || nombrePersona || empleado?.usuario?.nombrePersona || empleado?.usuario?.user_nom || "-";
}

/** Primer perfil del empleado. */
export function perfilEmpleadoIso(empleado: Empleado): string {
  const perfil = empleado?.listaPerfiles?.[0];
  return perfil?.nombre || perfil?.pNombre || "";
}

/** Puesto del empleado o "Sin puesto". */
export function puestoEmpleadoIso(empleado: Empleado): string {
  return empleado?.puesto?.puesto || "Sin puesto";
}
